import {ErrorHandler, Inject, Injectable} from '@angular/core';
import {AppConfigService, FrontendAppSwitch, Role, UserAuthService, UserService} from '@lifeislife/lifeislife-domain';
import {BehaviorSubject, EMPTY, Observable, throwError} from 'rxjs';
import {distinctUntilChanged, filter, map, publishReplay, refCount, tap} from 'rxjs/operators';
import {DOCUMENT, Location} from '@angular/common';
import {Router} from '@angular/router';
import {fromPromise} from 'rxjs/internal-compatibility';
import {OAuthService} from 'angular-oauth2-oidc';

@Injectable({
  providedIn: 'root',
})
export class LoginRouteService {

  redirectUrl: string;
  lastUsedMail$: Observable<string>;

  recoveringSession$ = new BehaviorSubject<boolean>(false);
  recoverSessionError: string;


  // recover password flow
  recovering$ = new BehaviorSubject<boolean>(false);
  recoverError: string | null;

  // login/password flow
  authenticatingPassword$ = new BehaviorSubject<boolean>(false);
  passwordError: string | null;

  // mail flow
  mailLoginInProgress$ = new BehaviorSubject<boolean>(false);
  mailLoginError: string | null;


  expandedElement = new BehaviorSubject<'mail' | 'password' | null>(null);

  constructor(
    private configService: AppConfigService,
    private userAuthService: UserAuthService,
    @Inject(DOCUMENT)
    private document: Document,
    private location: Location,
    private userService: UserService,
    private router: Router,
    private oAuthService: OAuthService,
    private errorHandler: ErrorHandler,
  ) {
    this.lastUsedMail$ = this.userAuthService.state$.pipe(
      map(state => state.lastEmail),
      distinctUntilChanged(),
      filter(s => s != null),
    );

    // See https://github.com/manfredsteyer/angular-oauth2-oidc/issues/625
    this.recoveringSession$.next(true);
    this.restoreSession$()
      .subscribe(loggedIn => {
          this.recoveringSession$.next(false);
          if (loggedIn) {
            this.redirectOnLoggedIn();
          }
        },
        e => {
          console.error(e);
          this.recoveringSession$.next(false);
          this.recoverSessionError = e;
          this.errorHandler.handleError(e);
          this.oAuthService.revokeTokenAndLogout();
        }, () => {
          // this.recoveringSession$.next(false);
        });
  }

  recoverPassword$(contactMail: string) {
    this.recoverError = null;
    if (contactMail == null || contactMail.length === 0) {
      const message = `Email invalide`;
      this.recoverError = message;
      return throwError(new Error(message));
    }
    const origin = this.document == null ? null : this.document.location.origin;
    const curPath = this.location.prepareExternalUrl('/');
    const url = `${origin}${curPath}`;

    this.recovering$.next(true);
    return this.userService.requestEmailValidationCode$(contactMail, url, true, Role.CUSTOMER).pipe(
      tap(code => this.onRecoverSuccess(code),
        error => this.onRecoverError(error)),
    );
  }

  authenticateMail$(contactMail: string) {
    if (contactMail == null || contactMail.length === 0) {
      const message = `Email invalide`;
      this.recoverError = message;
      return throwError(new Error(message));
    }
    this.mailLoginError = null;

    const origin = this.document == null ? null : this.document.location.origin;
    const searchParam = this.document == null ? null : this.document.location.search;
    const curPath = this.location.prepareExternalUrl(`/${searchParam}`);
    const url = `${origin}${curPath}`;

    this.mailLoginInProgress$.next(true);
    return this.userService.requestEmailValidationCode$(contactMail, url, false, Role.CUSTOMER).pipe(
      tap((uri) => this.onEmailValidationSent(uri),
        error => this.onEmailValidationError(error)),
    );
  }

  authenticatePassword$(contactMail: string, password: string) {
    this.passwordError = null;
    if (contactMail == null || contactMail.length === 0
      || password == null || password.length === 0) {
      const message = `Email ou mot de passe invalide`;
      this.passwordError = message;
      return throwError(new Error(message));
    }
    this.authenticatingPassword$.next(true);

    return this.userAuthService.authenticateBasic$(contactMail, password, true).pipe(
      tap(user => this.onLoginSuccess(),
        error => this.onLoginError(error)),
    );
  }

  toggleExpanded(element: 'mail' | 'password' | null) {
    const expanded = this.expandedElement.getValue() === element;
    this.setExpanded(element, !expanded);
  }

  setExpanded(element: 'mail' | 'password' | null, exapanded: boolean) {
    if (exapanded) {
      this.expandedElement.next(element);
    } else {
      this.expandedElement.next(null);
    }
  }

  isCollapsed$(element: 'mail' | 'password'): Observable<boolean> {
    return this.expandedElement.pipe(
      map(e => e !== element),
      publishReplay(1), refCount(),
    );
  }

  isRecovering() {
    return this.recovering$.getValue();
  }

  redirectOnLoggedIn() {
    if (this.redirectUrl != null) {
      this.router.navigateByUrl(this.redirectUrl)
        .catch(e => this.handleRedirectError(e));
    } else {
      this.router.navigate(['/products'], {
        queryParams: {},
      })
        .catch(e => console.warn(e));
    }
  }

  private onLoginSuccess() {
    this.authenticatingPassword$.next(false);
    this.redirectOnLoggedIn();
  }

  private onLoginError(error: any) {
    this.authenticatingPassword$.next(false);
    if (error && error.status && error.status === 404) {
      const message = `Adresse email incorrecte`;
      // Local logoff: we are not authenticated to keycloak yet, and since the user had the time
      // to submit the form, keycloak session restoring already occured. We can assume there
      // is not need to logout by keycloak, and avoid the redirect.
      this.userAuthService.deauthenticate(message, true);
      this.passwordError = message;
      return;
    } else if (error && error.status && error.status === 0) {
      this.passwordError = `Impossible de contacter le serveur`;
    } else if (error.message) {
      this.passwordError = `${error.message}`;
    } else {
      this.passwordError = `Erreur inconnue`;
    }
  }

  private onRecoverSuccess(uri: string) {
    const consoleDebug = this.configService.isSwitchCurrentlyEnabled(FrontendAppSwitch.front_dev_debug);
    if (consoleDebug) {
      console.log('Email link: ' + uri);
    }
    this.recovering$.next(false);
  }

  private onRecoverError(error: any) {
    this.recovering$.next(false);

    if (error && error.status && error.status === 404) {
      const message = `Adresse email incorrecte`;
      // Local logoff: we are not authenticated to keycloak yet, and since the user had the time
      // to submit the  form, keycloak session restoring already occured. We can assume there
      // is not need to logout by keycloak, and avoid the redirect.
      this.userAuthService.deauthenticate(message, true);
      this.recoverError = message;
      return;
    }
  }


  private onEmailValidationSent(uri: string) {
    const consoleDebug = this.configService.isSwitchCurrentlyEnabled(FrontendAppSwitch.front_dev_debug);
    if (consoleDebug) {
      console.log('Email link: ' + uri);
    }
    this.mailLoginInProgress$.next(false);

  }

  private onEmailValidationError(error: any) {
    this.mailLoginInProgress$.next(false);
    if (error && error.status && error.status === 404) {
      const message = `Adresse email incorrecte`;
      // Local logoff: we are not authenticated to keycloak yet, and since the user had the time
      // to submit the  form, keycloak session restoring already occured. We can assume there
      // is not need to logout by keycloak, and avoid the redirect.
      this.userAuthService.deauthenticate(message, true);
      this.mailLoginError = message;
    }
  }

  private handleRedirectError(error: any) {
    console.warn(error);
    this.router.navigate(['/products'])
      .catch(e => console.warn(e));
  }

  private restoreSession$() {
    if (this.oAuthService.hasValidIdToken()) {
      // Bypass on refresh with valid token
      this.redirectOnLoggedIn();
      return EMPTY;
    } else {
      return fromPromise(this.oAuthService.tryLogin());
    }
  }
}
